import {
  collection,
  getDocs,
  addDoc,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { db } from "@/firebaseConfig";
import type { ActionContext, Module } from "vuex";

export interface EventTeam {
  id: string;
  title: string;
  selfAssignableOnEvents?: boolean;
  maxUsersOnEvent?: number;
  hideFromEvents?: boolean;
}

export interface EventTeamsState {
  byEvent: Record<string, EventTeam[]>;
  fetching: Record<string, boolean>;
  error: string | null;
}

const state = (): EventTeamsState => ({
  byEvent: {},
  fetching: {},
  error: null,
});

type RootState = unknown;

const eventTeamsModule: Module<EventTeamsState, RootState> = {
  namespaced: true,

  getters: {
    teams:
      (state) =>
      (eventId: string): EventTeam[] =>
        state.byEvent[eventId] ?? [],
    isFetching:
      (state) =>
      (eventId: string): boolean =>
        !!state.fetching[eventId],
  },

  mutations: {
    SET_FETCHING(
      state,
      { eventId, value }: { eventId: string; value: boolean },
    ) {
      state.fetching[eventId] = value;
    },
    SET_ERROR(state, msg: string | null) {
      state.error = msg;
    },
    SET_TEAMS(
      state,
      { eventId, teams }: { eventId: string; teams: EventTeam[] },
    ) {
      state.byEvent[eventId] = teams;
    },
    ADD_TEAM(state, { eventId, team }: { eventId: string; team: EventTeam }) {
      const list = state.byEvent[eventId] ?? [];
      state.byEvent[eventId] = [...list, team];
    },
  },

  actions: {
    async fetchEventTeams({ commit }, { eventId }: { eventId: string }) {
      commit("SET_FETCHING", { eventId, value: true });
      commit("SET_ERROR", null);

      try {
        const col = collection(db, `events/${eventId}/teams`);
        const snap = await getDocs(col);
        const teams: EventTeam[] = snap.docs.map((doc) => {
          const data = doc.data() as Partial<EventTeam>;
          return {
            id: doc.id,
            title: String(data.title ?? ""),
            selfAssignableOnEvents: Boolean(data.selfAssignableOnEvents),
            maxUsersOnEvent: Number(data.maxUsersOnEvent ?? 0),
            hideFromEvents: Boolean(data.hideFromEvents),
          };
        });
        commit("SET_TEAMS", { eventId, teams });
      } catch (err: any) {
        commit("SET_ERROR", err?.message ?? String(err));
      } finally {
        commit("SET_FETCHING", { eventId, value: false });
      }
    },

    async addTeam(
      { commit },
      {
        eventId,
        title,
        selfAssignableOnEvents = true,
        maxUsersOnEvent = 0,
      }: {
        eventId: string;
        title: string;
        selfAssignableOnEvents?: boolean;
        maxUsersOnEvent?: number;
      },
    ) {
      const col = collection(db, `events/${eventId}/teams`);
      const payload = {
        title: title.trim(),
        selfAssignableOnEvents,
        maxUsersOnEvent,
      };
      const ref = await addDoc(col, payload);
      const team: EventTeam = {
        id: ref.id,
        title: payload.title,
        selfAssignableOnEvents,
        maxUsersOnEvent,
      };
      commit("ADD_TEAM", { eventId, team });
      return team;
    },
  },
};

export default eventTeamsModule;
